import { getScheduleNowWallClock } from "../../../lib/time";
import { useAuth } from "../../../context/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/ui/confirm-modal";
import { useToast } from "../../../components/ui/toast";
import { core } from "../../../lib/api";
import { asArray, useAgents, useWorkflows } from "../../home/queries";
import type { VoiceOption } from "../../home/types";
import { StepContacts, StepRouting, StepSchedule } from "./steps";

export interface RowError { row: number; field: string; reason: string }
export interface WizardState {
  agentId: string; version: string; voiceId: string; did: string; campaign: string;
  langRouting: boolean; fallback: string;
  interruption: boolean; bgNoise: boolean; ambience: any[]; backgroundAudio: any[];
  dltId: string; header: string; dndScrub: boolean; windows: string[];
  contactSource: "csv" | "manual"; fileName: string | null; fileSize: number | null;
  rawRows: Record<string, string>[]; csvHeaders: string[]; mapping: Record<string, string>; excludedRowIndices: number[];
  normalized: Record<string, string>[]; rejectedContacts: Record<string, string>[]; duplicateContacts?: Record<string, string>[]; rowErrors: RowError[]; deduped: number; dndRemoved: number;
  manualRows: Record<string, string>[];
  csvCountryCode: string;
  mode: "now" | "later"; at: string; timezone: string; concurrency: number; budget: string;
  useAlternateNumber: boolean; multiAgentConfigId: string;
  isRetryLocked: boolean;
  step: number;
}

const INIT: WizardState = {
  agentId: "", version: "", voiceId: "", did: "", campaign: "", langRouting: true, fallback: "end",
  interruption: true, bgNoise: true, ambience: [], backgroundAudio: [],
  dltId: "", header: "", dndScrub: true, windows: ["08-12", "12-17", "17-21"],
  contactSource: "csv", fileName: null, fileSize: null, rawRows: [], csvHeaders: [], mapping: {}, excludedRowIndices: [],
  normalized: [], rejectedContacts: [], duplicateContacts: [], rowErrors: [], deduped: 0, dndRemoved: 0, manualRows: [{}],
  csvCountryCode: "+91",
  mode: "now", at: "", timezone: "Asia/Kolkata", concurrency: 50, budget: "",
  useAlternateNumber: false, multiAgentConfigId: "",
  isRetryLocked: false,
  step: 0,
};

export function CreateBatchWizard() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const agents = useAgents();
  const { user } = useAuth() as any;
  const workflows = useWorkflows();
  const [s, setS] = useState<WizardState>(INIT);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const patch = (next: Partial<WizardState>) => setS((current) => ({ ...current, ...next }));
  const keyRef = useRef(crypto.randomUUID());
  const preselectionHandled = useRef(false);
  const retryHandled = useRef(false);

  useEffect(() => {
    if (user?.timezone) {
      patch({ timezone: user.timezone });
    }
  }, [user?.timezone]);

  useEffect(() => {
    if (retryHandled.current) return;
    const routeState = location.state as {
      retryContacts?: Array<{
        contact_id?: string;
        name?: string;
        phone_number?: string;
        metadata?: Record<string, unknown>;
      }>;
      retryContext?: {
        agent_id?: string;
        version_id?: string;
      } | null;
      retryLocked?: boolean;
    } | null;
    const retryContacts = routeState?.retryContacts;
    if (!Array.isArray(retryContacts) || retryContacts.length === 0) return;

    const rows = retryContacts.map((contact) => ({
      ...Object.fromEntries(
        Object.entries(contact.metadata || {}).map(([key, value]) => [key, String(value ?? "")]),
      ),
      contact_id: String(contact.contact_id || ""),
      name: String(contact.name || ""),
      phone_number: String(contact.phone_number || ""),
    }));
    patch({
      agentId: String(routeState?.retryContext?.agent_id || ""),
      version: String(routeState?.retryContext?.version_id || ""),
      multiAgentConfigId: "",
      contactSource: "manual",
      manualRows: rows,
      normalized: rows,
      rejectedContacts: [],
      duplicateContacts: [],
      rawRows: [],
      csvHeaders: [],
      mapping: {},
      rowErrors: [],
      deduped: 0,
      dndRemoved: 0,
      isRetryLocked: !!routeState?.retryLocked,
    });
    retryHandled.current = true;
    preselectionHandled.current = true;
    toast.success(`${rows.length} contact${rows.length === 1 ? "" : "s"} loaded for retry`);
  }, [location.state, toast]);

  useEffect(() => {
    if (preselectionHandled.current) return;
    const workflowId = searchParams.get("workflow") || searchParams.get("multi_agent_config_id");
    const agentId = searchParams.get("agent");

    if (workflowId) {
      if (workflows.data === undefined) return;
      const workflow = workflows.data.find((item) => (item.config_id || item.id) === workflowId);
      if (workflow) {
        patch({
          agentId: "",
          multiAgentConfigId: workflowId,
          version: String(workflow.version_id || ""),
        });
      }
      preselectionHandled.current = true;
      return;
    }

    if (agentId) {
      if (agents.data === undefined) return;
      const agent = agents.data.find((item) => item.agent_id === agentId);
      if (agent?.multi_agent_config_id) {
        if (workflows.data === undefined) return;
        const workflow = workflows.data.find(
          (item) => (item.config_id || item.id) === agent.multi_agent_config_id,
        );
        if (workflow) {
          patch({
            agentId: "",
            multiAgentConfigId: agent.multi_agent_config_id,
            version: String(workflow.version_id || ""),
          });
        }
      } else if (agent) {
        patch({ agentId, multiAgentConfigId: "", version: "" });
      }
    }
    preselectionHandled.current = true;
  }, [agents.data, searchParams, workflows.data]);

  const valid = [
    !!(s.agentId || s.multiAgentConfigId) && !!s.version && !!s.voiceId && !!s.did,
    s.normalized.length > 0,
    s.mode === "now" || (!!s.at && new Date(s.at) > new Date()),
  ];
  const allValid = valid.every(Boolean);

  const voices = useQuery({
    queryKey: ["voice", "options"],
    queryFn: () => core<any>("/api/v1/voice/options").then((d) => asArray<VoiceOption>(d, "voices")),
    staleTime: Infinity,
  });

  const launch = useMutation({
    mutationFn: () => {
      const selectedVoice = voices.data?.find((voice) => (voice.local_voice_name || (voice as any).voice_id) === s.voiceId);
      const selectedAgent = agents.data?.find((agent) => agent.agent_id === s.agentId);
      const selectedWorkflow = workflows.data?.find(
        (workflow) => (workflow.config_id || workflow.id) === s.multiAgentConfigId,
      );
      const normalizedVoice = s.voiceId.trim().toLowerCase();

      let scheduleDate = "";
      let scheduleTime = "";
      let scheduleTimezone = s.timezone || "Asia/Kolkata";

      if (s.mode === "now") {
        const targetInstant = new Date(Date.now() + 2 * 60 * 1000);
        try {
          const formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone: scheduleTimezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          const parts = formatter.formatToParts(targetInstant);
          const partMap: Record<string, string> = {};
          for (const p of parts) partMap[p.type] = p.value;
          scheduleDate = `${partMap.year}-${partMap.month}-${partMap.day}`;
          scheduleTime = `${partMap.hour}:${partMap.minute}`;
        } catch {
          scheduleDate = [
            targetInstant.getFullYear(),
            String(targetInstant.getMonth() + 1).padStart(2, "0"),
            String(targetInstant.getDate()).padStart(2, "0"),
          ].join("-");
          scheduleTime = `${String(targetInstant.getHours()).padStart(2, "0")}:${String(targetInstant.getMinutes()).padStart(2, "0")}`;
        }
      } else {
        [scheduleDate, scheduleTime] = s.at.split("T");
      }

      return core<{ schedule_id: string; idempotent?: boolean }>("/api/v1/schedules", {
        method: "PUT",
        body: JSON.stringify({
          idempotency_key: keyRef.current,
          agent_id: s.agentId || selectedWorkflow?.agent_id || "",
          agent_name: selectedWorkflow?.agent_name || selectedWorkflow?.name || selectedAgent?.agent_name || selectedAgent?.name || "",
          multi_agent_config_id: s.multiAgentConfigId || undefined,
          version_id: s.version,
          version_name: selectedWorkflow?.version_name || s.version,
          voice: s.voiceId,
          custom_tts: normalizedVoice === "sia" || normalizedVoice === "meera",
          gender: selectedVoice?.gender || null,
          did_number_id: s.did,
          use_alternate_number: s.useAlternateNumber,
          campaign: s.campaign.trim() || "NA",
          interruption: s.interruption,
          background_noise: s.bgNoise,
          ambience: s.bgNoise ? s.ambience : [],
          background_audio: s.bgNoise ? s.backgroundAudio : [],
          contacts: [...s.normalized, ...s.rejectedContacts],
          schedule_date: scheduleDate,
          schedule_time: scheduleTime,
          schedule_timezone: scheduleTimezone,
          batch_call_concurrency: s.concurrency,
        }),
      });
    },
    onSuccess: (data) => {
      data.idempotent ? toast.info("Batch already created") : toast.success("Batch launched", "Track it in Active Batches.");
      nav("/batch");
    },
    onError: (error) => toast.danger("Launch failed", (error as Error).message),
  });

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-6 p-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-line bg-elevated p-6 shadow-sm">
          <h2 className="mb-5 text-base font-bold uppercase tracking-wider text-ink-3">1. AI &amp; Routing</h2>
          <StepRouting s={s} patch={patch} />
        </div>

        <div className="flex flex-col rounded-2xl border border-line bg-elevated p-6 shadow-sm">
          <h2 className="mb-5 text-base font-bold uppercase tracking-wider text-ink-3">2. Contacts</h2>
          <div className="flex-1">
            <StepContacts s={s} patch={patch} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-elevated p-6 shadow-sm">
        <h2 className="mb-5 text-base font-bold uppercase tracking-wider text-ink-3">3. Scheduling</h2>
        <StepSchedule
          s={s}
          patch={patch}
          onLaunch={() => launch.mutate()}
          launching={launch.isPending}
          allValid={allValid}
        />
      </div>

      <div className="flex justify-start">
        <button onClick={() => setConfirmCancel(true)} className="text-sm font-semibold text-ink-3 hover:text-ink hover:underline">
          Cancel
        </button>
      </div>

      {confirmCancel && (
        <ConfirmModal
          title="Leave batch setup?"
          body="Your current entries will be lost and you will return to Batch Workspace."
          confirmLabel="Leave setup"
          onClose={() => setConfirmCancel(false)}
          onConfirm={() => nav("/batch")}
        />
      )}
    </div>
  );
}
